import { Chart as ChartJS, Plugin } from 'chart.js';
import { ComparisonSeriesProps, CrosstableProps, CrosstableSignificance, Evolution, EvolutionLegendKind } from '../dashboardInterfaces';
import downIcon, { ReactComponent as DownIcon } from '../../../assets/evolutions/down.svg';
import negativeIcon, { ReactComponent as NegativeIcon } from '../../../assets/evolutions/negative.svg';
import positiveIcon, { ReactComponent as PositiveIcon } from '../../../assets/evolutions/positive.svg';
import upIcon, { ReactComponent as UpIcon } from '../../../assets/evolutions/up.svg';

const TEMPORAL_COMPARISON = 'TEMPORAL';
const CURRENT_SCORES_HIGHER = 'UP';

export const EVOLUTION_ICONS = {
    [Evolution.POSITIVE]: PositiveIcon,
    [Evolution.NEGATIVE]: NegativeIcon,
    [Evolution.UP]: UpIcon,
    [Evolution.DOWN]: DownIcon,
};

// Only a significant gap is shown. The backend reads its direction from the
// figure on screen, UP when it stands above the compared population.
export const getEvolution = (
    comparisonType: string | undefined,
    significance: ComparisonSeriesProps['significance'] | undefined,
    label: string,
): Evolution | null => {
    const result = significance?.results?.[label];
    if (!result?.isSignificant || !result.direction) {
        return null;
    }
    const isHigher = result.direction === CURRENT_SCORES_HIGHER;
    if (comparisonType === TEMPORAL_COMPARISON) {
        return isHigher ? Evolution.UP : Evolution.DOWN;
    }

    return isHigher ? Evolution.POSITIVE : Evolution.NEGATIVE;
};

// A cross table column is always read against its TOTAL column, never against
// another period, so only the positive and negative icons apply.
export const getCrosstableEvolution = (
    significance: CrosstableSignificance | null | undefined,
): Evolution | null => {
    const result = significance?.result;
    if (!result?.isSignificant || !result.direction) {
        return null;
    }

    return result.direction === CURRENT_SCORES_HIGHER ? Evolution.POSITIVE : Evolution.NEGATIVE;
};

// The legend of a card, only when it shows at least one icon: the same
// verdicts as the ones getEvolution and getCrosstableEvolution draw.
export const getEvolutionLegendKind = (data: any): EvolutionLegendKind | null => {
    const crosstableDatasets = (data as CrosstableProps | undefined)?.datasets;
    if (Array.isArray(crosstableDatasets) && crosstableDatasets.some((dataset) => dataset.datas?.some(
        (row) => getCrosstableEvolution(row.significance) !== null,
    ))) {
        return EvolutionLegendKind.TOTAL;
    }
    const series = Object.values<ComparisonSeriesProps>(data?.comparison?.results ?? {});
    const hasSignificantGap = series.some((serie) => Object.keys(serie?.significance?.results ?? {}).some(
        (label) => getEvolution(data.comparison.type, serie.significance, label) !== null,
    ));
    if (!hasSignificantGap) {
        return null;
    }

    return data.comparison.type === TEMPORAL_COMPARISON ? EvolutionLegendKind.PERIOD : EvolutionLegendKind.GLOBAL;
};

const createImage = (src: string) => {
    const image = new Image();
    image.src = src;

    return image;
};

const EVOLUTION_IMAGES: Record<Evolution, HTMLImageElement> = {
    [Evolution.POSITIVE]: createImage(positiveIcon),
    [Evolution.NEGATIVE]: createImage(negativeIcon),
    [Evolution.UP]: createImage(upIcon),
    [Evolution.DOWN]: createImage(downIcon),
};

const isImageReady = (image: HTMLImageElement) => image.complete && image.naturalWidth > 0;

// Tooltips have a dark background, their icons are drawn white.
const TOOLTIP_ICON_COLOR = '#ffffff';

// The icon takes the color of the text it follows, which the canvas still holds.
const getTextColor = (ctx: CanvasRenderingContext2D) => (
    typeof ctx.fillStyle === 'string' ? ctx.fillStyle : undefined
);
// The tinted copy is rasterized large and scaled down, so it stays sharp.
const TINTED_ICON_WIDTH = 64;
const tintedImages = new Map<string, HTMLCanvasElement>();

const getTintedImage = (evolution: Evolution, image: HTMLImageElement, color: string) => {
    const cacheKey = `${evolution}-${color}`;
    const cached = tintedImages.get(cacheKey);
    if (cached) {
        return cached;
    }
    const canvas = document.createElement('canvas');
    canvas.width = TINTED_ICON_WIDTH;
    canvas.height = Math.max(1, Math.round(TINTED_ICON_WIDTH * (image.naturalHeight / image.naturalWidth)));
    const tintContext = canvas.getContext('2d');
    if (!tintContext) {
        return image;
    }
    tintContext.drawImage(image, 0, 0, canvas.width, canvas.height);
    // Keeps the shape of the icon and replaces its color.
    tintContext.globalCompositeOperation = 'source-in';
    tintContext.fillStyle = color;
    tintContext.fillRect(0, 0, canvas.width, canvas.height);
    tintedImages.set(cacheKey, canvas);

    return canvas;
};

// The icon is `size` wide and keeps its own ratio, the minus sign stays a thin line.
export const drawEvolutionIcon = (ctx: CanvasRenderingContext2D, evolution: Evolution, x: number, centerY: number, size: number, color?: string) => {
    const image = EVOLUTION_IMAGES[evolution];
    if (!isImageReady(image)) {
        return;
    }
    const height = size * (image.naturalHeight / image.naturalWidth);
    ctx.drawImage(color ? getTintedImage(evolution, image, color) : image, x, centerY - (height / 2), size, height);
};

const ICON_SIZE_RATIO = 0.75;
const ICON_GAP_RATIO = 0.35;
const DEFAULT_FONT_PIXELS = 12;

export const getEvolutionIconSize = (fontPixels: number) => fontPixels * ICON_SIZE_RATIO;
export const getEvolutionIconGap = (fontPixels: number) => fontPixels * ICON_GAP_RATIO;

// Chart.js and its datalabels plugin only draw text. A private use character at
// the end of a label asks the plugin below to draw the icon right after it.
const EVOLUTION_MARKERS: Record<Evolution, string> = {
    [Evolution.POSITIVE]: '',
    [Evolution.NEGATIVE]: '',
    [Evolution.UP]: '',
    [Evolution.DOWN]: '',
};

const MARKED_EVOLUTIONS = Object.fromEntries(
    Object.entries(EVOLUTION_MARKERS).map(([evolution, marker]) => [marker, evolution as Evolution]),
);

export const withEvolutionMarker = (text: string, evolution: Evolution | null) => (
    evolution ? `${text}${EVOLUTION_MARKERS[evolution]}` : text
);

const splitEvolutionMarker = (text: string) => {
    const evolution = MARKED_EVOLUTIONS[text.slice(-1)];

    return evolution ? { text: text.slice(0, -1), evolution } : null;
};

const getFontPixels = (ctx: CanvasRenderingContext2D) => {
    const match = /(\d+(?:\.\d+)?)px/.exec(ctx.font);

    return match ? Number(match[1]) : DEFAULT_FONT_PIXELS;
};

// The icon is centred on the glyphs actually drawn: the middle baseline of the
// canvas is the one of the em box, which sits off the centre of the digits.
const getIconCenterY = (metrics: TextMetrics, y: number) => (
    y + ((metrics.actualBoundingBoxDescent - metrics.actualBoundingBoxAscent) / 2)
);

const getTextStartX = (ctx: CanvasRenderingContext2D, x: number, textWidth: number) => {
    if (ctx.textAlign === 'center') {
        return x - (textWidth / 2);
    }
    if (ctx.textAlign === 'right' || ctx.textAlign === 'end') {
        return x - textWidth;
    }

    return x;
};

type PatchedContext = {
    ctx: CanvasRenderingContext2D
    fillText: CanvasRenderingContext2D['fillText']
    measureText: CanvasRenderingContext2D['measureText']
    isDrawingTooltip: boolean
}

const patchedContexts = new WeakMap<ChartJS, PatchedContext>();

// Wraps the text calls of the chart canvas so that a marked label is measured
// with room for its icon, which keeps tooltips and labels wide enough, then
// drawn without its marker and followed by the icon. Positions stay the ones
// Chart.js computes, nothing of its layout is duplicated here.
export const evolutionIconPlugin: Plugin = {
    id: 'evolutionIcon',
    afterInit(chart) {
        const { ctx } = chart;
        const originalFillText = ctx.fillText.bind(ctx);
        const originalMeasureText = ctx.measureText.bind(ctx);

        const measureText = (text: string) => {
            const marked = splitEvolutionMarker(String(text));
            if (!marked) {
                return originalMeasureText(text);
            }
            const fontPixels = getFontPixels(ctx);
            const metrics = originalMeasureText(marked.text);

            return {
                width: metrics.width + getEvolutionIconGap(fontPixels) + getEvolutionIconSize(fontPixels),
                actualBoundingBoxAscent: metrics.actualBoundingBoxAscent,
                actualBoundingBoxDescent: metrics.actualBoundingBoxDescent,
                actualBoundingBoxLeft: metrics.actualBoundingBoxLeft,
                actualBoundingBoxRight: metrics.actualBoundingBoxRight,
            } as TextMetrics;
        };

        const fillText = (text: string, x: number, y: number, maxWidth?: number) => {
            const marked = splitEvolutionMarker(String(text));
            if (!marked) {
                originalFillText(text, x, y, maxWidth);

                return;
            }
            originalFillText(marked.text, x, y, maxWidth);
            const fontPixels = getFontPixels(ctx);
            const metrics = originalMeasureText(marked.text);
            const textWidth = metrics.width;
            drawEvolutionIcon(
                ctx,
                marked.evolution,
                getTextStartX(ctx, x, textWidth) + textWidth + getEvolutionIconGap(fontPixels),
                getIconCenterY(metrics, y),
                getEvolutionIconSize(fontPixels),
                patchedContexts.get(chart)?.isDrawingTooltip ? TOOLTIP_ICON_COLOR : getTextColor(ctx),
            );
        };

        ctx.measureText = measureText;
        ctx.fillText = fillText;
        patchedContexts.set(chart, { ctx, fillText, measureText, isDrawingTooltip: false });

        // A chart drawn before the icons are loaded draws them once they are.
        Object.values(EVOLUTION_IMAGES)
            .filter((image) => !isImageReady(image))
            .forEach((image) => image.addEventListener('load', () => {
                if (patchedContexts.has(chart)) {
                    chart.draw();
                }
            }, { once: true }));
    },
    beforeTooltipDraw(chart) {
        const patched = patchedContexts.get(chart);
        if (patched) {
            patched.isDrawingTooltip = true;
        }
    },
    afterTooltipDraw(chart) {
        const patched = patchedContexts.get(chart);
        if (patched) {
            patched.isDrawingTooltip = false;
        }
    },
    afterDestroy(chart) {
        const patched = patchedContexts.get(chart);
        if (!patched) {
            return;
        }
        // The canvas can outlive the chart, it gets its own methods back.
        const { ctx } = patched;
        if (ctx.fillText === patched.fillText) {
            delete (ctx as Partial<CanvasRenderingContext2D>).fillText;
        }
        if (ctx.measureText === patched.measureText) {
            delete (ctx as Partial<CanvasRenderingContext2D>).measureText;
        }
        patchedContexts.delete(chart);
    },
};
